/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import './PolynomialGraph.scss';
import { Chart as ChartJS, LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, Filler } from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import { BodyText } from '@vaisala-rockhopper/components';

interface PolynomialGraphProps {
    graphData: {
      'CCD': number[],
      'nD': number[],
      'nD_calc': number[],
      'nD_calcLine': number[],
      'A': number[],
      'CCD_calc': number[]
    } | null
    graphLabels: number[]
    theme: string;
    canvasContainerHeight: number
    diffValues: number[]
}

ChartJS.register(Filler, LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip);

export const PolynomialGraph = ({
    graphData,
    graphLabels,
    theme,
    canvasContainerHeight,
    diffValues
}: PolynomialGraphProps) => {
  const chartRef = React.useRef();
  const [dataPointLabels, setDataPointLabels] = React.useState<number[]>([])
  const [datapoints, setDatapoints] = React.useState<{"x": number, "y": number}[]>([{"x": 0, "y": 0}])
  const [linedata, setLinedata] = React.useState<{"x": number, "y": number}[]>([])

  const colors: any = {
    grid: {
      border: theme === 'vai-theme-light' ? 'rgba(105, 105, 105, 1.0)' : 'rgba(54, 54, 54, 1.0)',
      tick:  theme === 'vai-theme-light' ? 'rgba(228, 232, 237, 1.0)' : 'rgba(54, 54, 54, 1.0)',
    },
    h2o2Concentration: {
      border: theme === 'vai-theme-light' ? 'rgba(54, 54, 54, 1.0)' : 'rgba(228, 232, 237, 1.0)',
    },
    relativeSaturation: {
      border: theme === 'vai-theme-light' ? 'rgba(255, 150, 0, 0.8)' : 'rgba(255, 150, 0, 0.8)',
      bg: theme === 'vai-theme-light' ? 'rgba(255, 176, 26, 0.8)' : 'rgba(255, 176, 26, 0.8)',
    },
    graph: {
      point: theme === 'vai-theme-light' ? 'rgba(0, 154, 200, 1)' : 'rgba(0, 103, 149, 0.8)',
      bg: theme === 'vai-theme-light' ? 'rgba(26, 180, 226, 0.8)' : 'rgba(0, 129, 175, 0.8)',
      danger: theme === 'vai-theme-light' ? 'rgba(180, 10, 50, 1)' : 'rgba(0, 129, 175, 0.8)',
    },
    condensation: {
      border: theme === 'vai-theme-light' ? 'rgba(180, 10, 50, 1.0)' : 'rgba(180, 10, 50, 1.0)',
      bg: theme === 'vai-theme-light' ? 'rgba(180, 10, 50, 1.0)' : 'rgba(180, 10, 50, 1.0)',
    },
    activePhase: {
      bg: theme === 'vai-theme-light' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },
    tooltip: {
      font: theme === 'vai-theme-light' ? '#ffffff' : 'rgba(181, 181, 181, 1.0)',
      border: theme === 'vai-theme-light' ? '#003462' : 'rgba(255, 255, 255, 0.25)',
      bg: theme === 'vai-theme-light' ? '#003462' : '#101719',
    },
  };

  const [canvasHeight, setCanvasHeight] = React.useState(0)

  React.useEffect(() => {
    setCanvasHeight(canvasContainerHeight)
  }, [canvasContainerHeight])

  const resetGraphData = () => {
    setDataPointLabels([])
    setDatapoints([{"x": 0, "y": 0}])
    setLinedata([])
  }

  React.useEffect(() => {
    if (graphData !== null) {
      if (graphData.CCD.length > 0) {
          let graphDataCopy = {...graphData}
          graphDataCopy.CCD = graphDataCopy.CCD.reverse();
          graphDataCopy.nD = graphDataCopy.nD.reverse();
          graphDataCopy.nD_calcLine = graphDataCopy.nD_calcLine.reverse();
          graphDataCopy.A = graphDataCopy.A.reverse();
          graphDataCopy.CCD_calc = graphDataCopy.CCD_calc.reverse();

          let fullDataCalc: {"x": number, "y": number}[] = [];
          let fullData: {"x": number, "y": number}[] = [];
          let labels: number[] = [];
          graphDataCopy.CCD.forEach((val, i) => {
            fullData[i] = {x: val, y: graphDataCopy.nD[i]};
            labels[i] = graphDataCopy.A[i]
          })
          graphDataCopy.CCD_calc.forEach((val, i) => {
            fullDataCalc[i] = {x: val, y: graphDataCopy.nD_calcLine[i]};
          })
        setDatapoints(fullData)
        setLinedata(fullDataCalc)
        setDataPointLabels(graphLabels.reverse())
      }
    } else {
      resetGraphData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData])

  React.useEffect(() => {
    const oldData = {...data}
    oldData.labels = dataPointLabels;
    oldData.datasets[0].data = linedata;
    oldData.datasets[1].data = datapoints;
    setData(oldData)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datapoints])

  React.useEffect(() => {
    const oldData = {...data}
    oldData.datasets[1].borderColor = (context: any, options: any) => {
        const index = context.dataIndex;
        const value = diffValues[index]
        return Math.abs(value) >= 0.0004 ? colors.graph.danger : colors.graph.point
    }
    // setData(oldData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diffValues])

  // const afterDrawPlugin = {
  //   id: 'afterDraw',
  //   afterDraw: (chart: any) => {
  //     if (chart.tooltip?._active?.length) {
  //       let x = chart.tooltip._active[0].element.x;
  //       let yAxis = chart.scales.y;
  //       let ctx = chart.ctx;
  //       ctx.save();
  //       ctx.beginPath();
  //       ctx.setLineDash([6, 4]);
  //       ctx.moveTo(x, yAxis.top);
  //       ctx.lineTo(x, yAxis.bottom);
  //       ctx.lineWidth = 2;
  //       ctx.strokeStyle = 'rgb(255, 150, 0)';
  //       ctx.stroke();
  //       ctx.restore();
  //     }
  //   }
  // }

  // Graph data and options
  const [data, setData] = React.useState({
    labels: dataPointLabels,
    datasets: [
      {
          data: linedata,
          label: "Line",
          backgroundColor: colors.graph.point,
          borderColor: colors.graph.point,
          borderWidth: 1,
          pointRadius: 0,
          pointHitRadius: 0,
          showLine: true
      },
      {
          data: datapoints,
          label: "Dots",
          backgroundColor: ['rgba(0, 0, 0, 0)'],
          borderColor: (context: any, options: any) => {
            const index = context.dataIndex;
            const value = diffValues[index]
            return Math.abs(value) >= 0.0004 ? colors.graph.danger : colors.graph.point
          },
          borderWidth: 2,
      },
    ]
  });


  const [options, ] = React.useState({
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
    },
    indexAxis: "y" as "y",
    scales: {
        x: {
            min: 0,
            max: 100,
            grid: {
              borderColor: colors.grid.border,
              tickColor: colors.grid.tick
            },
            ticks: {
              color: colors.grid.tick,
            }
        },
        y: {
            min: 1.29,
            // max: 1.75,
            ticks: {
                stepSize: 0.01,
                color: colors.grid.tick,
            },
            reverse: true,
            grid: {
              borderColor: colors.grid.border,
              tickColor: colors.grid.tick
            },
        }
    }
  })

  const yAxisTitle = (
    <div className="graph__y-axis-title-container">
      <div className="left">
        <BodyText>nD</BodyText>
      </div>
    </div>
  );
  const xAxisTitle = (
    <div className="graph__x-axis-title-container">
      <div className="right">
          <div className="bottom">
            <BodyText>CCD</BodyText>
          </div>
      </div>
    </div>
  )

  return (
    <div className={`graph graph--desktop graph--active'} ${theme}`}>
      <div>
        <div className="graph__grid">
          <div className="graph__grid-item">
                {yAxisTitle}
                {datapoints.length > 0 && (
                  <div id="canvas" style={{"height":canvasHeight}}>
                    {/* <Line ref={chartRef} data={data} options={options} plugins={[afterDrawPlugin]} redraw /> */}
                    <Scatter ref={chartRef} data={data} options={options} redraw />
                  </div>
                )}
                {xAxisTitle}
                {/* {renderCustomLabels()} */}
            <div className="gtm-content gtm-content--desktop-calculator" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolynomialGraph;
